import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigate = useNavigate();

  const navigationItems = [
    {
      label: 'Platform Overview',
      path: '/platform-overview-dashboard',
      icon: 'BarChart3',
      tooltip: 'Comprehensive platform KPIs and system health monitoring'
    },
    {
      label: 'Revenue Intelligence',
      path: '/revenue-intelligence-dashboard',
      icon: 'TrendingUp',
      tooltip: 'Financial analytics and billing performance tracking'
    },
    {
      label: 'Tenant Analytics',
      path: '/tenant-management-analytics',
      icon: 'Users',
      tooltip: 'Comprehensive tenant health monitoring and subscription lifecycle'
    },
    {
      label: 'Usage Monitoring',
      path: '/usage-analytics-performance-monitoring',
      icon: 'Activity',
      tooltip: 'Platform utilization analytics and performance tracking'
    }
  ];

  const secondaryItems = [
    {
      label: 'Support Operations',
      path: '/support-operations-intelligence',
      icon: 'HeadphonesIcon',
      tooltip: 'Customer support analytics and administrative activity monitoring'
    }
  ];

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    navigate("/login", { replace: true });
  };

  const isActiveRoute = (path) => location.pathname === path;

  const Logo = () => (
    <Link to="/platform-overview-dashboard" className="flex items-center space-x-3">
      <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
        <Icon name="Shield" size={20} color="white" />
      </div>
      <div className="flex flex-col">
        <span className="text-lg font-semibold text-text-primary tracking-tight">
          SuperAdmin
        </span>
        <span className="text-xs text-text-secondary font-medium">
          Dashboard
        </span>
      </div>
    </Link>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-100 bg-card border-b border-border shadow-elevation-1">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo */}
        <Logo />

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1">
          {navigationItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`
                flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-150 ease-dashboard
                ${isActiveRoute(item.path)
                  ? 'bg-primary/10 text-primary border-l-2 border-primary' :'text-text-secondary hover:text-text-primary hover:bg-muted/50'
                }
              `}
              title={item.tooltip}
            >
              <Icon name={item.icon} size={16} />
              <span className="hidden xl:inline">{item.label}</span>
            </Link>
          ))}

          {/* More Menu */}
          <div className="relative group">
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center space-x-2 text-text-secondary hover:text-text-primary"
            >
              <Icon name="MoreHorizontal" size={16} />
              <span className="hidden xl:inline">More</span>
            </Button>
            
            {/* Dropdown Menu */}
            <div className="absolute right-0 top-full mt-2 w-56 bg-popover border border-border rounded-lg shadow-elevation-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-dashboard">
              <div className="py-2">
                {secondaryItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      flex items-center space-x-3 px-4 py-2 text-sm transition-colors duration-150
                      ${isActiveRoute(item.path)
                        ? 'bg-primary/10 text-primary' :'text-text-secondary hover:text-text-primary hover:bg-muted/50'
                      }
                    `}
                    title={item.tooltip}
                  >
                    <Icon name={item.icon} size={16} />
                    <span>{item.label}</span>
                  </Link>
                ))}
                <div className="border-t border-border my-2"></div>
                <Link
                  to="/settings"
                  className="flex items-center space-x-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-muted/50 transition-colors duration-150"
                >
                  <Icon name="Settings" size={16} />
                  <span>Settings</span>
                </Link>
                <Link
                  to="/help"
                  className="flex items-center space-x-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-muted/50 transition-colors duration-150"
                >
                  <Icon name="HelpCircle" size={16} />
                  <span>Help & Support</span>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* User Profile & Actions */}
        <div className="flex items-center space-x-3">
          {/* Notifications */}
          <Button variant="ghost" size="sm" className="relative">
            <Icon name="Bell" size={18} />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-error rounded-full pulse-alert"></span>
          </Button>

          {/* User Menu */}
          <div className="relative group">
            <Button variant="ghost" size="sm" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-secondary to-accent rounded-full flex items-center justify-center">
                <Icon name="User" size={16} color="white" />
              </div>
              <Icon name="ChevronDown" size={14} className="hidden lg:inline" />
            </Button>
            
            {/* User Dropdown */}
            <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-lg shadow-elevation-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-dashboard">
              <div className="py-2">
                <div className="px-4 py-2 border-b border-border">
                  <p className="text-sm font-medium text-text-primary">Admin User</p>
                  <p className="text-xs text-text-secondary">admin@superadmin.com</p>
                </div>
                <Link
                  to="/profile"
                  className="flex items-center space-x-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-muted/50 transition-colors duration-150"
                >
                  <Icon name="User" size={16} />
                  <span>Profile</span>
                </Link>
                <Link
                  to="/preferences"
                  className="flex items-center space-x-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-muted/50 transition-colors duration-150"
                >
                  <Icon name="Settings" size={16} />
                  <span>Preferences</span>
                </Link>
                <div className="border-t border-border my-2"></div>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-3 px-4 py-2 text-sm text-error hover:bg-error/10 transition-colors duration-150 w-full text-left"
                >
                  <Icon name="LogOut" size={16} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Icon name={mobileMenuOpen ? "X" : "Menu"} size={20} />
          </Button>
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 z-200 bg-background/95 backdrop-blur-sm">
          <div className="bg-card border-r border-border h-full w-80 shadow-elevation-4">
            <nav className="p-4 space-y-2">
              {[...navigationItems, ...secondaryItems].map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-150
                    ${isActiveRoute(item.path)
                      ? 'bg-primary/10 text-primary border-l-4 border-primary' :'text-text-secondary hover:text-text-primary hover:bg-muted/50'
                    }
                  `}
                >
                  <Icon name={item.icon} size={20} />
                  <span>{item.label}</span>
                </Link>
              ))}
              
              <div className="border-t border-border my-4"></div>
              
              <Link
                to="/settings"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center space-x-3 px-4 py-3 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:bg-muted/50 transition-all duration-150"
              >
                <Icon name="Settings" size={20} />
                <span>Settings</span>
              </Link>
              
              <Link
                to="/help"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center space-x-3 px-4 py-3 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:bg-muted/50 transition-all duration-150"
              >
                <Icon name="HelpCircle" size={20} />
                <span>Help & Support</span>
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;